import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment-timezone';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon-dim.svg';

interface TimelineDot {
  projectName: string;
  slaTime: string;
  actualCompletionTime?: string | null;
  slaStatus: string;
  colorCode: string;
  dataAvailability?: string | number;
}

interface CustomLinearProgressProps {
  timeline: TimelineDot[];
  isPBNA?: boolean;
}

// Responsive inset: 7vw or max 50px
const DOT_INSET: any = 'clamp(16px, 7vw, 50px)';
const DOT_SIZE = 'clamp(14px, 2vw, 24px)';

const LinearProgress: React.FC<CustomLinearProgressProps> = ({ timeline, isPBNA = false }) => {
  const theme = useTheme();
  const colors = theme.palette.dataPipeline.accordionWidget;

  if (!timeline.length) return null;

  let allStatus: string | null = null;
  if (timeline.every((dot) => dot.slaStatus === 'Not Started')) {
    allStatus = 'Not Started';
  } else if (timeline.every((dot) => dot.slaStatus === 'In Progress')) {
    allStatus = 'In Progress';
  }

  let lastStatusIdx = timeline.reduce((acc, dot, idx) => {
    if (dot.slaStatus === 'SLA Met' || dot.slaStatus === 'SLA Missed') {
      return idx;
    }
    return acc;
  }, -1);

  let nextIdx = -1;
  if (lastStatusIdx !== -1) {
    const lastDot = timeline[lastStatusIdx];
    const lastSlaMoment = moment.tz(lastDot.slaTime, 'YYYY-MM-DD HH:mm', 'America/Chicago');

    // 1. Next dot after lastStatusIdx (not a completed state)
    nextIdx = timeline.findIndex(
      (dot, idx) =>
        idx > lastStatusIdx && dot.slaStatus !== 'SLA Met' && dot.slaStatus !== 'SLA Missed',
    );

    // 2. If not found, next dot by slaTime (not a completed state)
    if (nextIdx === -1) {
      nextIdx = timeline.findIndex(
        (dot, idx) =>
          idx !== lastStatusIdx &&
          dot.slaTime &&
          dot.slaStatus !== 'SLA Met' &&
          dot.slaStatus !== 'SLA Missed' &&
          moment.tz(dot.slaTime, 'YYYY-MM-DD HH:mm', 'America/Chicago').isAfter(lastSlaMoment),
      );
    }

    // 3. If still not found, first dot after lastStatusIdx with no slaTime and status '--'
    if (nextIdx === -1) {
      nextIdx = timeline.findIndex(
        (dot, idx) => idx > lastStatusIdx && !dot.slaTime && dot.slaStatus === '--',
      );
    }
  }

  // If all statuses are Not Started or In Progress, show only first dot direct value
  if (allStatus) {
    lastStatusIdx = -1;
    nextIdx = 0;
  }

  const dotCount = timeline.length;

  const shouldShowConnector = (isPBNA: boolean, projectName: string) =>
    (!isPBNA && projectName !== 'MDT') || isPBNA;

  const renderSlaTime = (dot: any, isPBNA: boolean) => {
    if (!isPBNA && dot.projectName === 'MDT') return ' - ';
    return `SLA Time: ${moment.utc(dot.slaTime).format('hh:mm A')} CST`;
  };

  const renderActualCompletion = (dot: any) => {
    if (dot.actualCompletionTime) {
      return `Actual Completion Time: ${moment(dot.actualCompletionTime)
        .utc()
        .format('hh:mm A')} CST`;
    }
    return null;
  };

  const renderCompletionInfo = (dot: any) => {
    if (dot.actualCompletionTime) {
      return (
        <Typography
          fontSize={{ xs: 12, sm: 13, md: 14 }}
          color={colors.text.secondary}
          fontStyle="italic"
          fontWeight={400}
        >
          {renderActualCompletion(dot)}
        </Typography>
      );
    }

    if (dot.slaStatus === 'SLA Missed') {
      return (
        <Typography
          fontSize={{ xs: 10, sm: 12, md: 14 }}
          color={colors.text.secondary}
          fontStyle="italic"
          fontWeight={400}
        >
          Actual Completion Time: Not Available{' '}
          <Tooltip
            title={
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: 11, sm: 12, md: 13 },
                  color: colors.text.tooltipPrimary,
                  fontWeight: 500,
                }}
              >
                Click on the hop for more details
              </Typography>
            }
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: `${colors.timelineTooltip?.background} !important`,
                  color: `${colors.timelineTooltip?.text} !important`,
                  borderRadius: '16px',
                  boxShadow: colors.timelineTooltip?.shadow,
                  px: 2,
                  py: 1.5,
                },
              },
              arrow: {
                sx: {
                  color: `${colors.timelineTooltip?.background} !important`,
                  '&:before': {
                    boxShadow: colors.timelineTooltip?.shadow,
                  },
                },
              },
            }}
          >
            <InfoIcon style={{ verticalAlign: 'text-bottom' }} />
          </Tooltip>
        </Typography>
      );
    }

    return null;
  };

  const renderDataAvailability = (dot: any) => {
    if (
      dot.dataAvailability === undefined ||
      dot.dataAvailability === null ||
      dot.dataAvailability === ''
    ) {
      return null;
    }

    return (
      <Typography
        fontSize={{ xs: 10, sm: 12, md: 14 }}
        fontStyle="italic"
        color={colors.text.primary}
        fontWeight={400}
        sx={{ mt: 0.5 }}
      >
        Data Availability: {dot.dataAvailability}
        {typeof dot.dataAvailability === 'number' ? '%' : ''}
      </Typography>
    );
  };

  const projectBadge = (dot: any) => (
    <Box
      sx={{
        mt: 1.2,
        bgcolor: `${dot.colorCode || colors.background.statusMuted} !important`,
        color: colors.text.inverse,
        fontSize: { xs: 11, sm: 13, md: 16 },
        fontWeight: 700,
        px: { xs: 1.5, sm: 2.5, md: 3 },
        py: 0.7,
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: { xs: 60, sm: 90, md: 140, lg: 190 },
        lineHeight: 1,
        marginBottom: { xs: '6px', sm: '10px' },
        textTransform: 'uppercase',
        textAlign: 'center',
        wordBreak: 'break-word',
      }}
    >
      {dot.projectName}
    </Box>
  );

  const tooltipContent = (dot: any) => (
    <Box
      sx={{
        textAlign: 'center',
        minWidth: { xs: 160, sm: 200, md: 220 },
      }}
    >
      <Typography
        fontSize={{ xs: 11, sm: 13, md: 16 }}
        fontWeight={500}
        color={colors.text.primary}
      >
        {renderSlaTime(dot, isPBNA)}
      </Typography>
      {renderCompletionInfo(dot)}
      {renderDataAvailability(dot)}
      {projectBadge(dot)}
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        minWidth: 220,
        height: { xs: 48, sm: 60, md: 70 },
        px: { xs: 1, sm: 2, md: 3 },
        boxSizing: 'border-box',
        overflow: 'visible',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: 0,
          right: 0,
          height: 'clamp(6px, 1vw, 12px)',
          backgroundColor: colors.background.timelineBar,
          borderRadius: '4px',
          transform: 'translateY(-50%)',
          zIndex: 1,
        }}
      />

      {timeline.map((dot, idx) => {
        let left;
        if (dotCount === 1) {
          left = '50%';
        } else if (dotCount > 1) {
          const percent = idx / (dotCount - 1);
          left = `calc(${DOT_INSET} + ${percent} * (100% - 2 * ${DOT_INSET}))`;
        }

        const textAlign = 'center';
        const showConnector = shouldShowConnector(isPBNA, dot.projectName);

        if (idx === lastStatusIdx && lastStatusIdx !== -1) {
          return (
            <Box key={`${dot.projectName}-${idx}`}>
              {showConnector && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '22px',
                    left,
                    width: '1px',
                    height: '10px',
                    backgroundColor: colors.background.timelineConnector,
                    transform: 'translateX(-50%)',
                    zIndex: 3,
                  }}
                />
              )}
              <Box
                sx={{
                  position: 'absolute',
                  left,
                  top: '35px',
                  transform: 'translateX(-50%)',
                  textAlign,
                  width: { xs: '90px', sm: '120px', md: 'max-content' },
                  maxWidth: { xs: '100px', sm: '140px', md: '240px' },
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  ...(dotCount !== 1 && idx === 0 && { left: 0, transform: 'none' }),
                  ...(dotCount !== 1 &&
                    idx === dotCount - 1 && { right: 0, left: 'auto', transform: 'none' }),
                }}
              >
                <Typography
                  fontSize={{ xs: 10, sm: 12, md: 14 }}
                  fontWeight={500}
                  color={colors.text.primary}
                >
                  {renderSlaTime(dot, isPBNA)}
                </Typography>
                {renderCompletionInfo(dot)}
                {renderDataAvailability(dot)}
                {projectBadge(dot)}
              </Box>
            </Box>
          );
        } else if (idx === nextIdx && nextIdx !== -1) {
          return (
            <Box key={`${dot.projectName}-${idx}`}>
              {showConnector && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-3px',
                    left,
                    width: '1px',
                    height: { xs: '10px', sm: '12px', md: '10px' },
                    backgroundColor: colors.background.timelineConnector,
                    transform: 'translateX(-50%)',
                    zIndex: 3,
                  }}
                />
              )}
              <Box
                sx={{
                  position: 'absolute',
                  left,
                  top:
                    !dot.actualCompletionTime && dot.slaStatus !== 'SLA Missed'
                      ? '-88px'
                      : '-108px',
                  transform: 'translateX(-50%)',
                  textAlign,
                  width: 'max-content',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  ...(dotCount !== 1 && idx === 0 && { left: 0, transform: 'none' }),
                  ...(dotCount !== 1 &&
                    idx === dotCount - 1 && { right: 0, left: 'auto', transform: 'none' }),
                }}
              >
                <Typography
                  fontSize={{ xs: 10, sm: 12, md: 14 }}
                  fontWeight={500}
                  color={colors.text.primary}
                >
                  {renderSlaTime(dot, isPBNA)}
                </Typography>
                {renderCompletionInfo(dot)}
                {renderDataAvailability(dot)}
                {projectBadge(dot)}
              </Box>
            </Box>
          );
        } else {
          return (
            <Box
              key={`${dot.projectName}-${idx}`}
              sx={{
                position: 'absolute',
                top: '20%',
                left,
                zIndex: 2,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Tooltip
                placement="top"
                arrow
                title={tooltipContent(dot)}
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: `${colors.timelineTooltip?.background} !important`,
                      borderRadius: '16px',
                      boxShadow: '0px 10px 30px #00000033',
                      px: { xs: 1.5, sm: 2, md: 2.5 },
                      py: { xs: 1.25, sm: 1.5, md: 2 },
                    },
                  },
                  arrow: {
                    sx: {
                      color: `${colors.timelineTooltip?.background} !important`,
                      '&:before': {
                        boxShadow: colors.timelineTooltip?.shadow,
                      },
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    width: DOT_SIZE,
                    height: DOT_SIZE,
                    borderRadius: '50%',
                    bgcolor: `${dot.colorCode || colors.background.statusMuted}`,
                    border: `2px solid ${colors.background.timelineDotBorder}`,
                    boxShadow: `0 0 0 2px ${colors.background.timelineDotRing}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'width 0.2s, height 0.2s',
                  }}
                />
              </Tooltip>
            </Box>
          );
        }
      })}
    </Box>
  );
};

export default LinearProgress;
